import ApiModel           from '@widesk-core/models/ApiModel';
import FileModel          from '@models/website/StonecodeFileFileModel';
import { makeObservable } from 'mobx';
import { observable }     from 'mobx';
import { action }         from 'mobx';
import ApiCollection      from '@widesk-core/models/ApiCollection';
import _uniqBy            from 'lodash/uniqBy';

type Item = {
	source: ApiModel;
	loading: boolean;
	loaded: boolean;
	files: FileModel[];
}

type FetchOptions = {
	force?: boolean;
	fetchCollection?: boolean; // Si une collection est liée au model source, les Files seront récupérés pour toute la collection
}

class FileStore {
	public itemDictionary: Record<Urn, Item> = {};

	constructor() {
		makeObservable(this, {
			itemDictionary: observable,
			setItemDictionary: action,
			removeItem: action,
		});
	}

	public setItemDictionary(key: Urn, item: Item) {
		this.itemDictionary[key] = item;
	}

	public removeItem(key: Urn) {
		delete this.itemDictionary[key];
	}

	public async fetchForModel(model: ApiModel, options?: FetchOptions) {
		const sources = options?.fetchCollection ?
			(model.collection as ApiCollection<ApiModel> | undefined)?.map(m => m) || [model] :
			[model];

		// On ajoute toutes les sources dans le dictionnaire (si elle n'existe pas déjà)
		sources.forEach(source => {
			if (!this.itemDictionary[source.urn]) {
				this.setItemDictionary(source.urn, { source, loading: false, loaded: false, files: [] });
			}
		});

		if (options?.force) {
			sources.forEach(source => {
				this.setItemDictionary(source.urn, {
					...this.itemDictionary[source.urn]!,
					loading: false,
					loaded: false,
					files: [],
				});
			});
		}

		const itemsToFetch = Object.values(this.itemDictionary).filter(item => !item.loading && !item.loaded);

		if (itemsToFetch.length) {
			itemsToFetch.forEach(item => this.setItemDictionary(item.source.urn, { ...item, loading: true }));

			const fileCollection = new ApiCollection(FileModel);
			await fileCollection
				.setRequiredFilter('fileStatus.reference', 'available')
				.listBy(itemsToFetch.map(item => item.source.urn), 'typeSource.source.entityUrn');
			await fileCollection.whenIsLoaded(f => [
				f.typeSource.source,
				f.typeSource.type,
				f.mimeType,
			]);

			itemsToFetch.forEach(item => {
				this.setItemDictionary(item.source.urn, {
					...item,
					loading: false,
					loaded: true,
					files: _uniqBy([
						...item.files,
						...fileCollection.filter(f => f.typeSource.source.entityUrn === item.source.urn),
					], f => f.id),
				});
			});
		}

		return this.itemDictionary[model.urn]?.files || [];
	}

	public getFilesForModel(model: ApiModel, options?: FetchOptions) {
		(() => setTimeout(() => this.fetchForModel(model, options)))();

		return this.itemDictionary[model.urn]?.files || [];
	}
}

export default new FileStore();